/**
 * Weekly Review tracked keyword preferences — catalog, draft selection, persistence.
 *
 * The watchlist is project-scoped on the server. localStorage is only a seed
 * when the server row is empty, then a cache after save.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getKeywordWatchlist, putKeywordWatchlist } from '../api/opportunities';
import { normalizeRecommendedKeywordsResponse } from '../utils/weeklyGlobalOpportunities';
import { resolveRecommendedKeywords } from '../utils/recommendedKeywordsResolver';
import { normalizeMonitorKeyword } from '../utils/alertKeywordRecommendations';
import {
  buildWeeklyReviewDraftSelection,
  buildWeeklyReviewKeywordCatalog,
  dedupeKeywordList,
  loadWeeklyReviewTrackedKeywords,
  mergeWeeklyReviewDraftSelection,
  prefsFromWatchlistResponse,
  resolveWeeklyReviewTrackedKeywords,
  saveWeeklyReviewTrackedKeywords,
  type WeeklyReviewTrackedKeywordsPrefs,
} from '../utils/weeklyReviewTrackedKeywords';

export function useWeeklyReviewTrackedKeywords(
  projectId: string | undefined,
  userId: string | undefined
) {
  const queryClient = useQueryClient();
  const [savedPrefs, setSavedPrefs] = useState<WeeklyReviewTrackedKeywordsPrefs | null>(() =>
    loadWeeklyReviewTrackedKeywords(projectId, userId)
  );
  const [draftSelected, setDraftSelected] = useState<string[]>([]);
  const [customInput, setCustomInput] = useState('');
  const [filterText, setFilterText] = useState('');

  const watchlistQuery = useQuery({
    queryKey: ['keyword-watchlist', projectId],
    queryFn: async () => {
      const remote = await getKeywordWatchlist(projectId!);
      if ((remote.selected_keywords ?? []).length > 0) {
        return prefsFromWatchlistResponse(remote);
      }
      const local = loadWeeklyReviewTrackedKeywords(projectId, userId);
      if (local && local.selectedKeywords.length > 0) {
        const seeded = await putKeywordWatchlist(projectId!, local.selectedKeywords);
        return prefsFromWatchlistResponse(seeded);
      }
      return prefsFromWatchlistResponse(remote);
    },
    enabled: !!projectId,
    staleTime: 30 * 60 * 1000,
  });

  useEffect(() => {
    if (watchlistQuery.data) {
      setSavedPrefs(watchlistQuery.data);
      return;
    }
    if (!watchlistQuery.isFetched) {
      setSavedPrefs(loadWeeklyReviewTrackedKeywords(projectId, userId));
    }
  }, [watchlistQuery.data, watchlistQuery.isFetched, projectId, userId]);

  const keywordsQuery = useQuery({
    queryKey: ['recommended-keywords', projectId],
    queryFn: async () =>
      normalizeRecommendedKeywordsResponse(await resolveRecommendedKeywords(projectId!)),
    enabled: !!projectId,
    staleTime: 30 * 60 * 1000,
  });

  const catalog = useMemo(
    () =>
      buildWeeklyReviewKeywordCatalog(
        keywordsQuery.data,
        savedPrefs?.selectedKeywords ?? []
      ),
    [keywordsQuery.data, savedPrefs]
  );

  useEffect(() => {
    setDraftSelected(buildWeeklyReviewDraftSelection(catalog, savedPrefs, keywordsQuery.data));
  }, [catalog, savedPrefs, keywordsQuery.data]);

  const trackedKeywords = useMemo(
    () => resolveWeeklyReviewTrackedKeywords(savedPrefs, keywordsQuery.data),
    [savedPrefs, keywordsQuery.data]
  );

  const draftSelectionSet = useMemo(
    () => new Set(draftSelected.map(normalizeMonitorKeyword)),
    [draftSelected]
  );

  const savedSelection = useMemo(
    () => dedupeKeywordList(savedPrefs?.selectedKeywords ?? []),
    [savedPrefs]
  );

  const savedSelectionSet = useMemo(
    () => new Set(savedSelection.map(normalizeMonitorKeyword)),
    [savedSelection]
  );

  const isDirty = useMemo(() => {
    if (draftSelected.length !== savedSelection.length) return true;
    const draftKeys = draftSelected.map(normalizeMonitorKeyword).sort();
    const savedKeys = savedSelection.map(normalizeMonitorKeyword).sort();
    return draftKeys.some((key, index) => key !== savedKeys[index]);
  }, [draftSelected, savedSelection]);

  const filteredCatalog = useMemo(() => {
    const query = filterText.trim().toLowerCase();
    if (!query) return catalog;
    return catalog.filter((entry) => entry.keyword.toLowerCase().includes(query));
  }, [catalog, filterText]);

  const toggleKeyword = useCallback((keyword: string) => {
    const key = normalizeMonitorKeyword(keyword);
    setDraftSelected((current) => {
      const has = current.some((entry) => normalizeMonitorKeyword(entry) === key);
      if (has) {
        return current.filter((entry) => normalizeMonitorKeyword(entry) !== key);
      }
      return dedupeKeywordList([...current, keyword.trim()]);
    });
  }, []);

  const selectAll = useCallback(() => {
    setDraftSelected(catalog.map((entry) => entry.keyword));
  }, [catalog]);

  const clearAll = useCallback(() => {
    setDraftSelected([]);
  }, []);

  const addCustomKeyword = useCallback(() => {
    const trimmed = customInput.trim();
    if (!trimmed) return false;

    const key = normalizeMonitorKeyword(trimmed);
    setDraftSelected((current) => {
      if (current.some((entry) => normalizeMonitorKeyword(entry) === key)) {
        return current;
      }
      return dedupeKeywordList([...current, trimmed]);
    });
    setCustomInput('');
    return true;
  }, [customInput]);

  const save = useCallback(async () => {
    if (!projectId) return false;

    const nextPrefs: WeeklyReviewTrackedKeywordsPrefs = {
      selectedKeywords: mergeWeeklyReviewDraftSelection(catalog, draftSelected),
      updatedAt: new Date().toISOString(),
    };
    const remote = await putKeywordWatchlist(projectId, nextPrefs.selectedKeywords);
    const persisted = prefsFromWatchlistResponse(remote);
    if (userId) {
      saveWeeklyReviewTrackedKeywords(projectId, userId, persisted);
    }
    setSavedPrefs(persisted);
    queryClient.setQueryData(['keyword-watchlist', projectId], persisted);
    return true;
  }, [catalog, draftSelected, projectId, queryClient, userId]);

  const resetDraft = useCallback(() => {
    setDraftSelected(buildWeeklyReviewDraftSelection(catalog, savedPrefs, keywordsQuery.data));
    setCustomInput('');
    setFilterText('');
  }, [catalog, savedPrefs, keywordsQuery.data]);

  return {
    catalog,
    filteredCatalog,
    draftSelected,
    draftSelectionSet,
    trackedKeywords,
    toggleKeyword,
    selectAll,
    clearAll,
    addCustomKeyword,
    customInput,
    setCustomInput,
    filterText,
    setFilterText,
    save,
    resetDraft,
    isDirty,
    isLoadingCatalog:
      keywordsQuery.isLoading ||
      keywordsQuery.isFetching ||
      watchlistQuery.isLoading ||
      watchlistQuery.isFetching,
    catalogError: keywordsQuery.error || watchlistQuery.error,
    refetchCatalog: keywordsQuery.refetch,
    hasSavedSelection: savedSelectionSet.size > 0,
    draftSelectedCount: draftSelected.length,
    trackedKeywordCount: trackedKeywords.length,
    catalogCount: catalog.length,
  };
}
